import { TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface TechnicalMetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  trend?: number;
  status?: 'success' | 'warning' | 'error';
}

const statusStyles = {
  success: 'border-green-200 bg-green-50 text-green-900',
  warning: 'border-yellow-200 bg-yellow-50 text-yellow-900',
  error: 'border-red-200 bg-red-50 text-red-900'
};

export function TechnicalMetricCard({ 
  title, 
  value, 
  subtitle, 
  trend, 
  status 
}: TechnicalMetricCardProps) {
  const getTrendIcon = () => {
    if (trend === undefined) return null;
    if (trend > 0) return <TrendingUp className="w-3 h-3 text-green-500" />;
    if (trend < 0) return <TrendingDown className="w-3 h-3 text-red-500" />;
    return null;
  };

  const getTrendText = () => {
    if (trend === undefined) return '';
    return trend > 0 ? `+${trend}%` : `${trend}%`;
  };

  const getTrendColor = () => {
    if (trend === undefined) return '';
    return trend > 0 ? 'text-green-600' : 'text-red-600';
  };

  return (
    <Card className={cn(
      'hover:shadow-sm transition-shadow',
      status && statusStyles[status]
    )}>
      <CardContent className="p-4">
        <div className="space-y-1">
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
          {trend !== undefined && (
            <div className={cn('flex items-center gap-1 text-xs font-medium', getTrendColor())}>
              {getTrendIcon()}
              <span>{getTrendText()}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}